// src/components/ProjectDetails.jsx
import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import "./styles/ProjectDetails.css";
import "../components/styles/FileActionModal.css";
import { Link, useNavigate } from "react-router-dom";
import VariableContext from "../contexts/VariableContext";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { useAuth } from "../providers/AuthProvider";
import CommonButton from "../elments/CommonButton";
import FileActionModal from "../components/FileActionModal";

// ─── Icon SVGs ───────────────────────────────────────────────────────────────

const commonIconProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  width: 18,
  height: 18,
};

const IconReplace = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);
const IconUpload = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const IconWarning = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconDatabase = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);
const IconCopy = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...commonIconProps}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Feature icons map
const FeatureIcons = {
  scripting: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  datacheck: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  datatables: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  ),
  banner: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" />
    </svg>
  ),
  weighting: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <circle cx="12" cy="5" r="3" /><path d="M6.5 8a7 7 0 0 0 0 8" /><path d="M17.5 8a7 7 0 0 1 0 8" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  ),
  dataanalysis: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  dashboard: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
};

// ─── Reusable DropZone ────────────────────────────────────────────────────────
const DropZone = ({ accept, onFileSelect, selectedFile, hint }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = React.useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  return (
    <div
      className={`fam-dropzone${dragging ? " fam-dropzone--dragging" : ""}${selectedFile ? " fam-dropzone--selected" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => e.target.files[0] && onFileSelect(e.target.files[0])}
        style={{ display: "none" }}
      />
      <div className="fam-dropzone-icon">{selectedFile ? "✅" : "📂"}</div>
      {selectedFile ? (
        <>
          <div className="fam-dropzone-text">File selected</div>
          <div className="fam-dropzone-filename">✓ {selectedFile.name}</div>
        </>
      ) : (
        <>
          <div className="fam-dropzone-text">Drop file here or click to browse</div>
          <div className="fam-dropzone-hint">{hint}</div>
        </>
      )}
    </div>
  );
};

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, color = "purple", label }) => (
  <div className="fam-progress">
    <div className="fam-progress-track">
      <div
        className={`fam-progress-fill${color === "green" ? " fam-progress-fill--green" : color === "orange" ? " fam-progress-fill--orange" : ""}`}
        style={{ width: `${value}%` }}
      />
    </div>
    {label && <div className="fam-progress-text">{label}</div>}
  </div>
);

// ─── Helper: get initials from email ─────────────────────────────────────────
const getInitials = (email = "") => {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProjectDetails = ({ project, projectid, projectOwner, onProjectsRefresh }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const currentUserEmail = user?.user?.email || user?.email || "";
  const isProjectOwner = !!currentUserEmail && !!projectOwner
    && currentUserEmail.toLowerCase() === projectOwner.toLowerCase();

  const {
    banners,
    savedWeightings,
    setSdataGroup,
    projectId,
    setdataGroup,
    setProjectId,
    setPID,
    setPName
  } = useContext(VariableContext);

  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // New state for search

  // ── Editable project name state ───────────────────────────────────────────
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [savingName, setSavingName] = useState(false);
  // The `project` prop (Home.js's selectedProjectDetails) isn't refreshed by
  // onProjectsRefresh — that only re-syncs the sidebar's project list — so a
  // rename would keep showing the old name here until the project was
  // re-selected. Track the renamed value locally until a different project
  // is loaded.
  const [renamedDisplayName, setRenamedDisplayName] = useState(null);

  // ── Modal flags ────────────────────────────────────────────────────────────
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showReuploadModal, setShowReuploadModal] = useState(false);

  // ── Replace SPSS state ────────────────────────────────────────────────────
  const [selectedReplaceFile, setSelectedReplaceFile] = useState(null);
  const [replaceFileUploading, setReplaceFileUploading] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState(0);

  // ── Re-upload state ───────────────────────────────────────────────────────
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploadMode_type, setReuploadModeType] = useState("truncate");
  const [reuploadLoading, setReuploadLoading] = useState(false);
  const [reuploadProgress, setReuploadProgress] = useState(0);
  const [reviseDatagroup, setReviseDatagroup] = useState(false);

  // ── Saved calculations (scenarios) state ─────────────────────────────────
  const [showScenariosModal, setShowScenariosModal] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [deletedBatches, setDeletedBatches] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [runningScenarios, setRunningScenarios] = useState(false);
  const [restoringBatchId, setRestoringBatchId] = useState(null);

  // ── Filter-cache rebuild state (post-upload cache warm) ──────────────────
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [cacheCounts, setCacheCounts] = useState({ done: 0, total: 0 });
  const cachePollRef = React.useRef(null);

  // stop polling if the component unmounts mid-rebuild
  useEffect(() => () => {
    if (cachePollRef.current) clearInterval(cachePollRef.current);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  const memoizedProject = useMemo(() => project, [project]);
  const { client, project: proj, filename, uploaded_time, database_name } = memoizedProject || {};

  // Keep the editable name in sync with the project prop, unless the user is
  // actively editing it (so an in-flight edit isn't clobbered by a refresh).
  useEffect(() => {
    if (!isEditingName) setEditedName(renamedDisplayName || proj?.name || "");
  }, [proj?.name, isEditingName, renamedDisplayName]);

  // Reset the local override when a different project is loaded, so a stale
  // rename from a previous project never leaks into the new one.
  useEffect(() => {
    setRenamedDisplayName(null);
  }, [projectid]);

  const startEditingName = useCallback(() => {
    setEditedName(renamedDisplayName || proj?.name || "");
    setIsEditingName(true);
  }, [proj, renamedDisplayName]);

  const cancelEditingName = useCallback(() => {
    setEditedName(renamedDisplayName || proj?.name || "");
    setIsEditingName(false);
  }, [proj, renamedDisplayName]);

  const handleSaveName = useCallback(async () => {
    const trimmed = editedName.trim();
    if (!trimmed) { toast.warning("Project name cannot be empty."); return; }
    if (trimmed === (renamedDisplayName || proj?.name || "")) { setIsEditingName(false); return; }
    setSavingName(true);
    try {
      const { data } = await apiService.post(`/api/projects/${projectId}/rename`, {
        name: trimmed,
        requested_by: currentUserEmail || projectOwner || "",
      });
      if (data.success) {
        toast.success("Project name updated!");
        if (typeof setPName === "function") setPName(trimmed);
        setRenamedDisplayName(trimmed);
        setIsEditingName(false);
        if (onProjectsRefresh) await onProjectsRefresh();
      } else {
        toast.error(data.message || "Failed to update project name.");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred while updating the project name.");
    } finally {
      setSavingName(false);
    }
  }, [editedName, proj, projectId, currentUserEmail, projectOwner, toast, setPName, onProjectsRefresh, renamedDisplayName]);

  const handleNameKeyDown = useCallback((e) => {
    if (e.key === "Enter") { e.preventDefault(); handleSaveName(); }
    else if (e.key === "Escape") { e.preventDefault(); cancelEditingName(); }
  }, [handleSaveName, cancelEditingName]);

  const numberOfQuestions = useMemo(() => {
    if (!memoizedProject) return 0;
    return memoizedProject.qgroup_json
      ? Object.keys(memoizedProject.qgroup_json).length
      : memoizedProject.sqgroup_json
        ? Object.keys(memoizedProject.sqgroup_json).length
        : 0;
  }, [memoizedProject]);

  // ── Filtered users based on search term ──────────────────────────────────
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const searchLower = searchTerm.toLowerCase().trim();
    return users.filter(userItem =>
      userItem.displayName?.toLowerCase().includes(searchLower) ||
      userItem.mail?.toLowerCase().includes(searchLower) ||
      userItem.email?.toLowerCase().includes(searchLower)
    );
  }, [users, searchTerm]);

  // ── Features ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization?.features?.length > 0) {
      setAllowedFeatures(organization.features);
    } else {
      setAllowedFeatures(["scripting", "datacheck", "datatables", "banner", "weighting", "dataanalysis", "dashboardbuilder", "crosstab"]);
    }
  }, [user]);

  // ── Modal close helpers ───────────────────────────────────────────────────
  const closeReplaceModal = useCallback(() => {
    setShowReplaceModal(false); setSelectedReplaceFile(null); setReplaceProgress(0);
  }, []);

  const closeReuploadModal = useCallback(() => {
    setShowReuploadModal(false); setReuploadFile(null); setReuploadProgress(0); setReviseDatagroup(false);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleReplaceFile = useCallback(async () => {
    if (!selectedReplaceFile) { toast.warning("Please select a file first."); return; }
    setReplaceFileUploading(true);
    setReplaceProgress(10);
    const formData = new FormData();
    formData.append("file", selectedReplaceFile);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));
    formData.append("user", projectOwner);
    try {
      setReplaceProgress(50);
      const { data: result } = await apiService.upload("/save-data-to-sql", formData);
      setReplaceProgress(100);
      if (result.success) {
        toast.success("SPSS file replaced successfully!");
        setTimeout(() => { closeReplaceModal(); window.location.reload(); }, 600);
      } else {
        toast.error("Error replacing file: " + result.message);
        setReplaceProgress(0);
      }
    } catch (error) {
      toast.error("Error replacing file: " + error.message);
      setReplaceProgress(0);
    } finally {
      setReplaceFileUploading(false);
    }
  }, [selectedReplaceFile, projectid, client, proj, projectOwner, closeReplaceModal, toast]);

  // ── Saved calculations (scenarios) handlers ───────────────────────────────
  const fetchScenarios = useCallback(async () => {
    if (!projectid) return;
    setScenariosLoading(true);
    try {
      const [{ data: scData }, { data: dbData }] = await Promise.all([
        apiService.get(`/scenarios/${projectid}`),
        apiService.get(`/deleted-batches/${projectid}`),
      ]);
      if (scData?.success) setScenarios(scData.scenarios || []);
      if (dbData?.success) setDeletedBatches(dbData.batches || []);
    } catch (error) {
      toast.error("Failed to load saved calculations: " + error.message);
    } finally {
      setScenariosLoading(false);
    }
  }, [projectid, toast]);

  const handleRunScenarios = useCallback(async () => {
    setRunningScenarios(true);
    const userEmail = user?.user?.email || user?.email || projectOwner || "";
    try {
      const { data } = await apiService.post("/run-scenarios", {
        projectId: projectid,
        user: userEmail,
      }, { timeout: 600000 }); // scenarios rewrite the SPSS file + sync SQL — can be slow
      if (data?.success) {
        const failed = (data.results || []).filter((r) => !r.success);
        if (failed.length === 0) {
          toast.success(data.message || "Saved calculations re-applied successfully!");
        } else {
          toast.warning(`${data.message}. Failed: ${failed.map((f) => f.keyName).join(", ")}`);
        }
        fetchScenarios();
      } else {
        toast.error(data?.error || "Failed to re-apply saved calculations");
      }
    } catch (error) {
      toast.error("Error re-applying saved calculations: " + error.message);
    } finally {
      setRunningScenarios(false);
    }
  }, [projectid, user, projectOwner, toast, fetchScenarios]);

  const handleToggleScenario = useCallback(async (sc) => {
    try {
      const { data } = await apiService.post("/scenarios/toggle", {
        scenarioId: sc.id,
        active: !sc.active,
      });
      if (data?.success) {
        setScenarios((prev) => prev.map((s) => (s.id === sc.id ? { ...s, active: !sc.active } : s)));
      }
    } catch (error) {
      toast.error("Failed to update saved calculation: " + error.message);
    }
  }, [toast]);

  const handleDeleteScenario = useCallback(async (sc) => {
    const ok = await confirm({
      title: "Delete saved calculation",
      message: `Remove the saved definition "${sc.keyName}" (${sc.typeLabel})? It will no longer be re-applied after future data uploads. Already-computed data is not changed.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await apiService.post("/scenarios/delete", { scenarioId: sc.id });
      if (data?.success) {
        setScenarios((prev) => prev.filter((s) => s.id !== sc.id));
        toast.success("Saved calculation removed");
      } else {
        toast.error(data?.error || "Failed to delete saved calculation");
      }
    } catch (error) {
      toast.error("Failed to delete saved calculation: " + error.message);
    }
  }, [confirm, toast]);

  const handleRestoreBatch = useCallback(async (batch) => {
    const ok = await confirm({
      title: "Restore deleted IDs",
      message: `Restore ${batch.matchedCount} deleted respondent(s) (matched on "${batch.varName}") back into the SPSS file and database tables?`,
      confirmText: "Restore",
      variant: "success",
    });
    if (!ok) return;
    setRestoringBatchId(batch.batchId);
    const userEmail = user?.user?.email || user?.email || projectOwner || "";
    try {
      const { data } = await apiService.post("/restore-batch", {
        projectId: projectid,
        batchId: batch.batchId,
        user: userEmail,
      }, { timeout: 600000 });
      if (data?.success) {
        toast.success(`Restored ${data.restoredRespondents} respondent(s)`);
        fetchScenarios();
      } else {
        toast.error(data?.error || "Restore failed");
      }
    } catch (error) {
      toast.error("Restore failed: " + error.message);
    } finally {
      setRestoringBatchId(null);
    }
  }, [confirm, projectid, user, projectOwner, toast, fetchScenarios]);

  // ── Filter-cache rebuild (same backend job as Data Tables' "Refresh Filter
  // Cache"): pre-warms banner_ids_cache for every banner, question filter,
  // custom row, row statement and flttb logic. Promise resolves when the
  // background job finishes so the post-upload flow can await it.
  const handleRefreshFilterCache = useCallback(() => new Promise((resolve) => {
    if (!projectid) { resolve(false); return; }
    setCacheRefreshing(true);
    setCacheCounts({ done: 0, total: 0 });

    const finish = (ok) => {
      if (cachePollRef.current) { clearInterval(cachePollRef.current); cachePollRef.current = null; }
      setCacheRefreshing(false);
      resolve(ok);
    };

    apiService.post("/refresh_filter_cache", { projectId: projectid })
      .then(({ data }) => {
        const jobId = data?.job_id;
        if (!jobId) throw new Error(data?.error || "No job id returned");
        let pollFailures = 0;
        cachePollRef.current = setInterval(async () => {
          try {
            const { data: status } = await apiService.get(`/refresh_filter_cache_progress/${jobId}`);
            pollFailures = 0;
            setCacheCounts({ done: status?.done ?? 0, total: status?.total ?? 0 });
            if (status?.status === "done") {
              toast.success(
                status.total
                  ? `Filter cache rebuilt for ${status.total} filter${status.total !== 1 ? "s" : ""}.`
                  : "Filter cache rebuilt — no filters found to cache."
              );
              finish(true);
            } else if (status?.status === "error" || status?.status === "not_found") {
              toast.error(`Filter cache rebuild failed: ${status?.error || "Unknown error"}`);
              finish(false);
            }
          } catch (_) {
            // transient network error — keep polling, but give up if the
            // progress endpoint keeps failing (job expired / server restarted)
            pollFailures += 1;
            if (pollFailures >= 5) {
              toast.error("Lost contact with the cache rebuild job — it may still be running on the server.");
              finish(false);
            }
          }
        }, 2000);
      })
      .catch((err) => {
        toast.error(`Filter cache rebuild failed: ${err.message}`);
        finish(false);
      });
  }), [projectid, toast]);

  const handleReuploadSpss = useCallback(async () => {
    if (!reuploadFile) { toast.warning("Please select an SPSS file first."); return; }
    if (!projectid) { toast.error("No project selected — cannot update data."); return; }

    setReuploadLoading(true);
    setReuploadProgress(10);

    const formData = new FormData();
    formData.append("file", reuploadFile);
    // send project_id explicitly under both keys used elsewhere in the codebase
    // (backend was falling back to create-new when client/project weren't present)
    formData.append("project_id", projectid);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));
    formData.append("upload_mode", reuploadMode_type);
    formData.append("revise_datagroup", reviseDatagroup ? "true" : "false");
    const userEmail = user?.user?.email || user?.email || "";
    formData.append("user_email", userEmail);
    formData.append("user", userEmail); // some endpoints read `user`, not `user_email`

    try {
      setReuploadProgress(50);
      const { data } = await apiService.upload("/save-data-to-sql", formData);
      setReuploadProgress(100);

      if (data.success) {
        // guard: if backend ever returns a different project_id than the one
        // we're currently on, something upstream created a new project instead
        // of updating this one — surface it loudly instead of silently switching.
        if (data.project_id && String(data.project_id) !== String(projectid)) {
          toast.error("Backend returned a different project id — data was NOT applied to the current project. Please check.");
          setReuploadLoading(false);
          return;
        }

        toast.success(`Data ${reuploadMode_type === "truncate" ? "replaced" : "appended"} successfully!`);

        if (data.datagroupRevised && data.qgroup) {
          setdataGroup(data.qgroup);
          toast.info("Datagroup was rebuilt from the new file.");
        }

        // keep projectId pinned to the current project (don't let anything downstream swap it)
        setProjectId(projectid);

        setTimeout(closeReuploadModal, 700);

        if ((data.scenarioCount || 0) > 0) {
          const apply = await confirm({
            title: "Re-apply saved calculations?",
            message: `${data.scenarioCount} saved calculation(s) (custom variables, recodes, custom rows, ID deletions, weighting) are stored for this project. Re-apply them to the ${reuploadMode_type === "truncate" ? "new" : "updated"} data now?`,
            confirmText: "Re-apply",
            cancelText: "Not now",
            variant: "primary",
          });
          if (apply) await handleRunScenarios();
        }

        // Offer to pre-compute the banner/filter ID caches on the new data
        // (asked AFTER saved calculations so caches reflect the final dataset)
        const warmCache = await confirm({
          title: "Rebuild filter caches?",
          message: "Pre-compute the banner ID cache and all saved filters (question filters, custom rows, row statements) on the uploaded data now? Tables will run faster later, but this can take a few minutes.",
          confirmText: "Rebuild caches",
          cancelText: "Skip",
          variant: "primary",
        });
        if (warmCache) await handleRefreshFilterCache();
      } else {
        toast.error("Error re-uploading: " + (data.message || "Unknown error"));
        setReuploadProgress(0);
      }
    } catch (error) {
      toast.error("Error re-uploading SPSS: " + error.message);
      setReuploadProgress(0);
    } finally {
      setReuploadLoading(false);
    }
  }, [reuploadFile, reuploadMode_type, reviseDatagroup, user, projectid, client, proj, closeReuploadModal, toast, confirm, handleRunScenarios, handleRefreshFilterCache, setdataGroup, setProjectId]);

  const fetchOfficeUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get("/office-users");
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCheckboxChange = useCallback((email) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  }, []);

  const handleShareProject = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    try {
      await apiService.post("/share-project", {
        projectId: projectid,
        user_ids: selectedUsers,
        client_name: client?.name || "",
        project_name: proj?.name || "",
        user: projectOwner,
      });
      toast.success("Project shared successfully!");
      setShowUsers(false);
      setSelectedUsers([]);
      setSearchTerm(""); // Reset search when sharing is complete
    } catch {
      toast.error("Failed to share project.");
    }
  }, [selectedUsers, projectid, client, proj, projectOwner, toast]);

  const handleScriptingClick = useCallback((e) => {
    e.preventDefault();
    if (!memoizedProject) return;
    if (memoizedProject.sqgroup_json) {
      setSdataGroup(memoizedProject.sqgroup_json);
      setProjectId(projectid);
    }
    navigate("/surveybuilder");
  }, [memoizedProject, projectid, setSdataGroup, setProjectId, navigate]);

  const handleDuplicate = async () => {
    if (!projectid) { toast.warning("Please select a project to duplicate."); return; }
    if (!window.confirm(`Duplicate "${proj?.name || "this project"}"? A new project will be created with a copy of its survey structure, banners, weighting, quotas and other settings.`)) return;
    setLoading(true);
    try {
      const { data: result } = await apiService.post(`/api/projects/${projectId}/duplicate`, {
        requested_by: currentUserEmail || projectOwner || "",
      });
      if (result.success) {
        toast.success(`Project duplicated as "${result.project_name}"!`);
        if (result.db_copy_error) {
          toast.warning("Project was duplicated, but its database could not be provisioned - contact support before uploading data.");
        }
        if (result.mongo_copy_error) {
          toast.warning("Project was duplicated, but copying survey/quota settings failed - check quotas and survey config on the new project.");
        }
        if (onProjectsRefresh) {
          // Re-runs /loginOffice to pull the fresh project list (incl. the
          // duplicate) into the sidebar without a full page reload.
          await onProjectsRefresh();
        } else {
          toast.info('Please refresh your projects list to see the duplicate.');
        }
      } else {
        toast.error(result.message || "Failed to duplicate project.");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred while duplicating the project.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!projectid) { toast.warning("Please select a project to delete."); return; }
    if (!isProjectOwner) { toast.warning("Only the project owner can delete this project."); return; }
    if (!window.confirm("Are you sure you want to delete this project? This action cannot be undone.")) return;
    setLoading(true);
    try {
      const { data: result } = await apiService.delete(
        `/api/projects/${projectId}/delete?requested_by=${encodeURIComponent(currentUserEmail)}`
      );
      if (result.success) {
        toast.success("Project deleted successfully!");
        window.location.reload();
      } else {
        toast.error(result.message || "Failed to delete project.");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred while deleting the project.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memoizedProject) {
      if (memoizedProject.qgroup_json) setdataGroup(memoizedProject.qgroup_json);
      if (memoizedProject.sqgroup_json) setSdataGroup(memoizedProject.sqgroup_json);
      if (projectid) setProjectId(projectid);
    }
  }, [memoizedProject, projectid, setdataGroup, setSdataGroup, setProjectId]);

  if (!memoizedProject) {
    return (
      <div className="project-details-noselect">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        Select a project to view its details
      </div>
    );
  }

  const features = user?.user?.organization?.features || user?.organization?.features || {};

  const featureList = [
    { key: "scripting", label: "Scripting", to: "/surveybuilder", onClick: handleScriptingClick },
    { key: "datacheck", label: "DV", to: "/datacheck" },
    { key: "datatables", label: "Data Tables", to: "/datatables" },
    { key: "banner", label: "Banner", to: "/banner" },
    { key: "weighting", label: "Weighting", to: "/weighting" },
    { key: "dataanalysis", label: "Data Analysis", to: "/dataanalysis" },
    { key: "dashboard", label: "Dashboard", to: "/dashboardbuilder" },
  ];

  // ─── RENDER ───────────────────────────────────────────────────────────────
  const busyLabel = runningScenarios
    ? "Re-applying saved calculations…"
    : cacheRefreshing
      ? `Rebuilding filter caches…${cacheCounts.total ? ` (${cacheCounts.done}/${cacheCounts.total})` : ""}`
      : null;

  return (
    <div className="project-details">

      {/* ── Busy overlay: shown while saved calculations replay or the filter
             cache rebuilds, so the user always sees a waiting indicator ── */}
      {busyLabel && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(17, 24, 39, 0.55)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 14,
        }}>
          <style>{`@keyframes pd-busy-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{
            width: 44, height: 44, borderRadius: "50%",
            border: "4px solid rgba(255,255,255,0.25)", borderTopColor: "#fff",
            animation: "pd-busy-spin 0.9s linear infinite",
          }} />
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>{busyLabel}</div>
          <div style={{ color: "#d1d5db", fontSize: 12 }}>
            Please keep this tab open — this can take a few minutes.
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="pd-header">
        <div className="pd-header-left">
          <div className="pd-avatar">{getInitials(projectOwner)}</div>
          <div>
            <div className="pd-header-title">{renamedDisplayName || proj?.name || "Project details"}</div>
            <div className="pd-header-subtitle">
              {client?.name || "—"} · {projectOwner}
            </div>
          </div>
        </div>
        <div className="pd-header-actions">
          <button
            className="pd-btn pd-btn--share"
            onClick={() => { setShowUsers(!showUsers); if (!showUsers) fetchOfficeUsers(); }}
          >
            <IconUsers /> Share project
          </button>
          <button className="pd-btn" onClick={handleDuplicate} disabled={loading}>
            <IconCopy /> Duplicate
          </button>
          {isProjectOwner && (
            <button
              className="pd-btn pd-btn--danger"
              onClick={handleDelete}
              disabled={loading}
            >
              <IconTrash /> Delete
            </button>
          )}
        </div>
      </div>

      {/* ── Quick actions (moved up for visibility & interaction) ── */}
      <div className="pd-section-label">Features</div>
      <div className="pd-features pd-features--cards">
        {featureList.map(({ key, label, to, onClick }) => {
          const Icon = FeatureIcons[key];
          if (!features[key]) return null;
          return (
            <Link
              key={key}
              to={to}
              className="pd-feature-link"
              onClick={onClick}
            >
              {Icon && <Icon />} {label}
            </Link>
          );
        })}
      </div>

      {/* ── Share accordion (just below header) ── */}
      {showUsers && (
        <div className="pd-share-box" style={{ marginBottom: "18px" }}>
          <div
            className="pd-share-header"
            onClick={() => setShowUsers(!showUsers)}
          >
            <div className="pd-share-header-left">
              <IconUsers /> Share with team members
            </div>
            <svg
              className={`pd-share-chevron${showUsers ? " pd-share-chevron--open" : ""}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <div className="pd-share-body">
            <h4>Select users to share with</h4>

            {/* ── SEARCH BAR ── */}
            <div className="pd-search-container">
              <div className="pd-search-input-wrapper">
                <IconSearch />
                <input
                  type="text"
                  className="pd-search-input"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="pd-search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="pd-search-results-count">
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""} found
                </div>
              )}
            </div>

            <div className="pd-user-list">
              {loading ? (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "8px 0" }}>Loading users…</div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((userItem, index) => (
                  <div key={index} className="pd-user-item">
                    <input
                      type="checkbox"
                      value={userItem.email}
                      checked={selectedUsers.includes(userItem.mail)}
                      onChange={() => handleCheckboxChange(userItem.mail)}
                    />
                    <div className="pd-user-item-info">
                      <span className="pd-user-item-name">{userItem.displayName}</span>
                      <span className="pd-user-item-email">{userItem.mail || userItem.email}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "16px 0", textAlign: "center" }}>
                  {searchTerm ? "No users match your search." : "No users found in this organization."}
                </div>
              )}
            </div>
            {selectedUsers.length > 0 && (
              <div className="pd-share-confirm">
                <button className="pd-btn pd-btn--share" onClick={handleShareProject}>
                  Confirm share ({selectedUsers.length})
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Project info ── */}
      <div className="pd-section-label">Project details</div>
      <div className="pd-info-table">

        <div className="pd-info-row">
          <span className="pd-info-label">Owner</span>
          <span className="pd-info-value">{projectOwner || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Client</span>
          <span className="pd-info-value">{client?.name || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Project name</span>
          {isEditingName ? (
            <span className="pd-info-value" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                onBlur={handleSaveName}
                autoFocus
                disabled={savingName}
                className="pd-header-title-input"
                style={{
                  fontSize: "inherit",
                  fontFamily: "inherit",
                  padding: "2px 6px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  minWidth: 160,
                }}
              />
              <button
                className="pd-btn"
                style={{ padding: "4px 8px" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSaveName}
                disabled={savingName}
                title="Save"
              >
                <IconCheck />
              </button>
              <button
                className="pd-btn"
                style={{ padding: "4px 8px" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelEditingName}
                disabled={savingName}
                title="Cancel"
              >
                <IconX />
              </button>
            </span>
          ) : (
            <span className="pd-info-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {renamedDisplayName || proj?.name || "—"}
              {isProjectOwner && (
                <button
                  onClick={startEditingName}
                  title="Rename project"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#9ca3af",
                    display: "inline-flex",
                    padding: 2,
                  }}
                >
                  <IconEdit />
                </button>
              )}
            </span>
          )}
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Database</span>
          <span className="pd-info-value mono">{database_name || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">File name</span>
          <span className="pd-info-value mono">{filename || "—"}</span>
          <div className="pd-info-actions">
            <button className="pd-btn pd-btn--action" onClick={() => setShowReplaceModal(true)}>
              <IconReplace /> Replace SPSS
            </button>
            <button className="pd-btn pd-btn--action" onClick={() => setShowReuploadModal(true)}>
              <IconUpload /> Re-upload
            </button>
          </div>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Upload time</span>
          <span className="pd-info-value">
            {uploaded_time ? new Date(uploaded_time).toLocaleString() : "—"}
          </span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Questions</span>
          <span className="pd-info-value">{numberOfQuestions}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Weighting</span>
          <span className="pd-info-value">
            <span className={`pd-status-chip ${savedWeightings ? "pd-chip-ok" : "pd-chip-na"}`}>
              {savedWeightings ? <><IconCheck /> Applied</> : "Not set"}
            </span>
          </span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Banners</span>
          <span className="pd-info-value">
            <span className={`pd-status-chip ${banners?.length ? "pd-chip-ok" : "pd-chip-na"}`}>
              {banners?.length ? <><IconCheck /> {banners.length} banner{banners.length > 1 ? "s" : ""}</> : "Not set"}
            </span>
          </span>
        </div>

      </div>

      {/* ── Saved calculations ── */}
      <hr className="pd-divider" />
      <div className="pd-section-label">Saved calculations</div>
      <div className="pd-validation-box">
        <div className="pd-validation-title">
          <IconDatabase /> Automatic recalculation
        </div>
        <div className="pd-validation-desc">
          Custom variables, recodes, custom rows, ID deletions and weighting are saved with
          the project and can be re-applied automatically after each data re-upload
          (weighting is recalculated last, on the full updated dataset).
        </div>
        <button
          className="pd-btn pd-btn--action"
          onClick={() => { setShowScenariosModal(true); fetchScenarios(); }}
        >
          <IconDatabase /> Manage saved calculations
        </button>
      </div>

      <hr className="pd-divider" />

      {/* ── Modals ── */}

      {/* MODAL 1 — Replace SPSS */}
      <FileActionModal
        isOpen={showReplaceModal}
        onClose={closeReplaceModal}
        type="replace"
        title="Replace SPSS file"
        icon={<IconReplace />}
        iconClass="warning"
      >
        <div className="fam-alert fam-alert--warning">
          <span className="fam-alert-icon"><IconWarning /></span>
          <span>
            This will replace the current SPSS file. All existing configurations, banners,
            and weightings will be preserved.
          </span>
        </div>
        <p className="fam-desc">
          Upload a new <code>.sav</code> file to replace <strong>{filename || "the current file"}</strong>.
          Ensure your new file has the same variable structure.
        </p>
        <DropZone accept=".sav,.SAV" onFileSelect={setSelectedReplaceFile} selectedFile={selectedReplaceFile} hint=".sav / .SAV files only" />
        {replaceProgress > 0 && (
          <ProgressBar value={replaceProgress} color="orange" label={replaceProgress === 100 ? "File replaced successfully!" : "Uploading..."} />
        )}
        <div className="fam-footer">
          <button className="fam-btn" onClick={closeReplaceModal} disabled={replaceFileUploading}>Cancel</button>
          <button className="fam-btn fam-btn--danger" onClick={handleReuploadSpss} disabled={!selectedReplaceFile || replaceFileUploading}>
            <IconReplace /> {replaceFileUploading ? "Replacing…" : "Confirm replace"}
          </button>
        </div>
      </FileActionModal>

      {/* MODAL 2 — Re-upload SPSS */}
      <FileActionModal
        isOpen={showReuploadModal}
        onClose={closeReuploadModal}
        type="reupload"
        title="Re-upload SPSS data"
        icon={<IconDatabase />}
        iconClass="purple"
      >
        <p className="fam-desc">Choose how to update the existing project data with your new SPSS file.</p>
        <div className="fam-radio-group">
          <label
            className={`fam-radio-card${reuploadMode_type === "truncate" ? " fam-radio-card--active" : ""}`}
            onClick={() => setReuploadModeType("truncate")}
          >
            <input type="radio" name="reupload_mode" value="truncate" checked={reuploadMode_type === "truncate"} onChange={() => setReuploadModeType("truncate")} />
            <div>
              <div className="fam-radio-label">Truncate</div>
              <div className="fam-radio-desc">Replace all existing rows with new data</div>
            </div>
          </label>
          <label
            className={`fam-radio-card${reuploadMode_type === "append" ? " fam-radio-card--active" : ""}`}
            onClick={() => setReuploadModeType("append")}
          >
            <input type="radio" name="reupload_mode" value="append" checked={reuploadMode_type === "append"} onChange={() => setReuploadModeType("append")} />
            <div>
              <div className="fam-radio-label">Append</div>
              <div className="fam-radio-desc">Add new rows to existing data</div>
            </div>
          </label>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "10px 0", cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={reviseDatagroup}
            onChange={(e) => setReviseDatagroup(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Revise datagroup from the new file</strong>
            <span style={{ display: "block", color: "#6b7280", fontSize: 12, marginTop: 2 }}>
              Rebuilds the question structure exactly like a first upload. Leave unticked to
              keep the current structure (default).
            </span>
          </span>
        </label>
        {reviseDatagroup && (
          <div className="fam-alert fam-alert--warning">
            <span className="fam-alert-icon"><IconWarning /></span>
            <span>
              All structure customizations will be replaced: custom-variable entries, converted
              question types, edited titles/labels and custom rows. Saved calculations can
              recreate the data columns, but their structure entries must be saved again.
            </span>
          </div>
        )}
        <DropZone accept=".sav,.SAV" onFileSelect={setReuploadFile} selectedFile={reuploadFile} hint=".sav / .SAV files only" />
        {reuploadProgress > 0 && (
          <ProgressBar value={reuploadProgress} color="purple" label={reuploadProgress === 100 ? "Upload complete!" : "Uploading…"} />
        )}
        <div className="fam-footer">
          <button className="fam-btn" onClick={closeReuploadModal} disabled={reuploadLoading}>Cancel</button>
          <button className="fam-btn fam-btn--primary" onClick={handleReuploadSpss} disabled={!reuploadFile || reuploadLoading}>
            <IconUpload /> {reuploadLoading ? "Uploading…" : reuploadMode_type === "truncate" ? "Confirm replace" : "Confirm append"}
          </button>
        </div>
      </FileActionModal>

      {/* MODAL 3 — Saved calculations */}
      <FileActionModal
        isOpen={showScenariosModal}
        onClose={() => setShowScenariosModal(false)}
        type="scenarios"
        title="Saved calculations"
        icon={<IconDatabase />}
        iconClass="purple"
      >
        <p className="fam-desc">
          Definitions below are stored with the project. Active ones are offered for
          re-apply after every data re-upload; you can also run them manually here.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            className="fam-btn fam-btn--primary"
            onClick={handleRunScenarios}
            disabled={runningScenarios || scenarios.filter((s) => s.active).length === 0}
          >
            {runningScenarios
              ? "Re-applying…"
              : `Run all active (${scenarios.filter((s) => s.active).length})`}
          </button>
          <button className="fam-btn" onClick={fetchScenarios} disabled={scenariosLoading}>
            {scenariosLoading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {scenariosLoading ? (
          <div style={{ fontSize: 13, color: "#9ca3af", padding: "10px 0" }}>Loading saved calculations…</div>
        ) : scenarios.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9ca3af", padding: "10px 0" }}>
            No saved calculations yet. Create custom variables, recodes, custom rows or
            apply weighting and they will appear here automatically.
          </div>
        ) : (
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            {scenarios.map((sc) => (
              <div
                key={sc.id}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={!!sc.active}
                  onChange={() => handleToggleScenario(sc)}
                  title="Active — included when calculations are re-applied"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {sc.keyName} <span style={{ fontWeight: 400, color: "#6b7280" }}>· {sc.typeLabel}</span>
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(sc.targetNames || []).join(", ")}
                    {sc.lastRunAt
                      ? ` · last run ${new Date(sc.lastRunAt).toLocaleString()}${sc.lastRunStatus && sc.lastRunStatus !== "success" ? ` — ${sc.lastRunStatus}` : ""}`
                      : ""}
                  </div>
                </div>
                <button
                  className="fam-btn"
                  style={{ padding: "4px 8px" }}
                  onClick={() => handleDeleteScenario(sc)}
                  title="Delete this saved definition"
                >
                  <IconTrash />
                </button>
              </div>
            ))}
          </div>
        )}

        {deletedBatches.length > 0 && (
          <>
            <p className="fam-desc" style={{ marginTop: 14 }}>
              <strong>Deleted ID batches</strong> — removed respondents are archived and can be restored.
            </p>
            <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              {deletedBatches.map((b) => (
                <div
                  key={b.batchId}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {b.varName} <span style={{ fontWeight: 400, color: "#6b7280" }}>· {b.matchedCount} respondent(s)</span>
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: 12 }}>
                      {b.createdAt ? new Date(b.createdAt).toLocaleString() : ""}
                      {b.createdBy ? ` · ${b.createdBy}` : ""}
                      {b.restored ? " · restored" : ""}
                    </div>
                  </div>
                  {!b.restored && (
                    <button
                      className="fam-btn fam-btn--success"
                      style={{ padding: "4px 10px" }}
                      onClick={() => handleRestoreBatch(b)}
                      disabled={restoringBatchId === b.batchId}
                    >
                      {restoringBatchId === b.batchId ? "Restoring…" : "Restore"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="fam-footer">
          <button className="fam-btn" onClick={() => setShowScenariosModal(false)}>Close</button>
        </div>
      </FileActionModal>

    </div>
  );
};

export default ProjectDetails;